import { ApprovalStatus } from '@hooks/useAlbumInfo/types';
import { useSWRFetch } from '@hooks/useSWRFetch';
import { TEN_MINUTES_IN_MS } from '@helpers/constants';

export interface ProductDetails {
  explicit: boolean;
  title: string;
  artist: string;
}

export interface ProfileProtectionApproval {
  status: ApprovalStatus;
  statusChangedAt?: string;
}

export interface ProfileProtection {
  uri: string;
  artistId: string;
  feedName: string;
  licensorName: string;
  productDetails: ProductDetails;
  approval: ProfileProtectionApproval;
  primaryReleaseId: string;
  productType: string;
  includedMedia: string;
  originalReleaseDate: string;
}

export interface ProfileProtectionPaginationContext {
  totalRows: string;
  currentPage: string;
  actualPageSize: string;
  lastPage: boolean;
}

export interface ProfileProtectionResponse {
  entries: ProfileProtection[];
  paginationContext: ProfileProtectionPaginationContext;
}

function buildProfileProtectionsUrl(
  approvalStatuses: ApprovalStatus[],
  limit: number,
  offset: number,
): string {
  const params = new URLSearchParams();

  for (const status of approvalStatuses) {
    params.append('approvalStatuses', status);
  }

  params.set('pagination.limit', String(limit));
  params.set('pagination.offset', String(offset));

  return `v2/profile-protections?${params.toString()}`;
}

export const useGetProfileProtectionData = (
  approvalStatuses: ApprovalStatus[] = [],
  limit = 10,
  page = 1,
) => {
  const offset = (page - 1) * limit;
  const url = buildProfileProtectionsUrl(approvalStatuses, limit, offset);

  const result = useSWRFetch<ProfileProtectionResponse>(
    { url },
    { dedupingInterval: TEN_MINUTES_IN_MS },
  );

  return {
    data: result.data?.entries ?? [],
    paginationContext: result.data?.paginationContext,
    isLoading: result.isLoading,
    error: result.error,
  };
};
